import { type AppDispatch, type AppThunk } from "features/Application/globaltypes/redux";
import { type DistributedOpUpdateParams } from "interfaces";
import {
  fetchFailure,
  insertVideoAssetSuccess,
  updateVideoAssetBegin,
  updateVideoAssetSuccess,
} from "features/Library/Videos/state/slices/videoOverviewSlice";
import { pluralize } from "utils/stringUtils";

import DataService from "features/Application/services/dataServices/typedDataService";
import backgroundTask from "features/BackgroundTasks/backgroundTask";

export const updateVideoAsset = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(updateVideoAssetBegin());
  try {
    const result = await DataService.assets.getVideoInfo(id);
    dispatch(updateVideoAssetSuccess(result.data));
  } catch (error) {
    dispatch(fetchFailure(error));
  }
};

export const insertVideoAsset =
  (
    params: { videoAssetId: number; position: number; needToRemoveItem: boolean; count: number },
    onSuccess: () => void,
  ) =>
  async (dispatch: AppDispatch) => {
    try {
      const result = await DataService.assets.getVideoInfo(params.videoAssetId);
      dispatch(
        insertVideoAssetSuccess({
          videoAsset: result.data,
          position: params.position,
          count: params.count,
          needToRemoveItem: params.needToRemoveItem,
        }),
      );
      onSuccess?.();
    } catch (error) {
      dispatch(fetchFailure(error));
    }
  };

export const deleteVideosAction =
  (ids: number[], fetchVideos: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    const getOperationProps = async () => {
      const { data } = await DataService.assets.deleteVideos(ids);
      return data;
    };

    const idsCount = ids.length;
    const entity = pluralize("Video", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "DeletionVideoAssets",
      title: `Deletion of Videos`,
      onCompleted: () => fetchVideos(),
      getOperationProps,
      successTransientMessage: `${entity} ${idsCount === 1 ? "has" : "have"} been deleted!`,
      failureTransientMessage: `${entity} delete failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };

export const changeVisibility =
  (ids: number[], visible: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    const idsCount = ids.length;
    const entity = pluralize("Video", idsCount);
    const asset = pluralize("Asset", idsCount);

    const updateParams: DistributedOpUpdateParams = {
      id: "VisibilityVideosDistributedOperation",
      title: `${visible ? "Showing" : "Hiding"} ${asset}`,
      getOperationProps: async () => {
        const { data } = await DataService.assets.changeVisibility(ids, visible);
        return data;
      },
      successTitle: `${entity} ${visible ? "Shown" : "Hidden"}!`,
      successTransientMessage: `${entity} ${idsCount === 1 ? "has" : "have"} been ${visible ? "shown" : "hidden"} successfully!`,
      failureTransientMessage: `${entity} ${visible ? "showing" : "hiding"} failed!`,
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };
